import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { AxiosError } from 'axios'
import { useMutation } from 'react-query'
import { isEmpty } from 'lodash'
import {
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  Tab,
  Tabs,
  TextField as MuiTextField,
  Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import {
  Cancel as CancelIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Launch as LaunchIcon,
} from '@material-ui/icons'

import {
  Mode,
  ReceivingEvent,
  AuditHandler,
  AuditScanItem,
  HandlerCallback,
} from '../../scanRouter/handlers'
import { AuditResultPlace, Generic, makeGeneric } from 'src/api/models'
import { GenericCard, SkuCard } from 'src/components/ModelCard'
import { useQueryPaginator } from 'src/components/Table'
import { AuditResultUnitLocListTable } from 'src/components/Table/list/AuditResultUnitLoc'
import { ContainerListTable } from 'src/components/Table/list/Container'
import { AuditResultPlaceListTable } from 'src/components/Table/list/AuditResultPlace'
import { Instructions } from 'src/modules/shared/components/Instructions'
import { skuit } from 'src/services/skuit'
import { urlFor } from 'src/routes'

import { SkuSelectionCard } from './SkuSelectionCard'


interface AuditDisplayProps {
  handler: AuditHandler
}

const SinglePlaceDisplay = (props: AuditDisplayProps) => {
  const { handler } = props

  const containerPaginator = useQueryPaginator(
    'audit-result-containers',
    skuit.sc.containers.list,
    {
      params: {
        resulting_audits: handler.audit.object.guid,
        resulting_audit_places: `${handler.audit.object.guid}:${handler.currentPlace?.object.guid}`,
        page_size: 'all',
        ordering: 'xid',
      },
    }
  )
  handler.containerPaginator = containerPaginator

  const { mutate: exit } = useMutation(async () => handler.clearPlace(), {
    onError: (error: Error) => {
      toast.error(`${error.message}`)
    },
  })

  const { mutate: restart } = useMutation(handler.restartPlace, {
    onSuccess: () => {
      toast.success(`You have re-started auditing the place.`)
    },
    onError: (error: AxiosError) => {
      toast.error(`Failed to reset. "${error.response?.data}"`)
    },
  })

  const { mutate: start } = useMutation(handler.startPlace, {
    onSuccess: () => {
      toast.success(`You have started auditing the place.`)
    },
    onError: (error: AxiosError) => {
      toast.error(`Failed to start. "${error.response?.data}"`)
    },
  })

  const { mutate: save } = useMutation((clearPlace: boolean) => handler.save(clearPlace), {
    onSuccess: () => {
      toast.success(`Saved inventory to Place in Audit.`)
    },
    onError: (error: AxiosError) => {
      if (error.message) {
        toast.error(error.message)
      } else if ((error as AxiosError).response) {
        toast.error(`Failed to save. "${error.response?.data}"`)
      }
    },
  })

  const { mutate: complete } = useMutation(() => handler.complete(), {
    onSuccess: () => {
      toast.success(`Saved inventory to Place in Audit and marked complete.`)
    },
    onError: (error: AxiosError | Error) => {
      if (error.message) {
        toast.error(error.message)
      } else if ((error as AxiosError).response) {
        toast.error(`Failed to mark complte. "${(error as AxiosError).response?.data}"`)
      }
    },
  })

  const { mutate: reopen } = useMutation(handler.uncomplete, {
    onSuccess: () => {
      toast.success(`You have re-opened auditing the place.`)
    },
    onError: (error: AxiosError) => {
      toast.error(`Failed to re-open for audit. "${error.response?.data}"`)
    },
  })

  const { mutate: reconcileCurrent } = useMutation(handler.reconcileCurrent, {
    onSuccess: () => {
      toast.success(`You have reconciled the place.`)
    },
    onError: (error: AxiosError) => {
      toast.error(`Failed to reconcile. "${error.response?.data}"`)
    },
  })

  const unitPaginator = useQueryPaginator(
    'audit-result-inventory',
    (config) => skuit.sc.audits.units.list(handler.audit.object.guid, config),
    {
      params: {
        audits: handler.audit.object.guid,
        audit_places: handler.currentPlace?.object.guid,
        containers: handler.currentContainer?.object.guid,
        page_size: 'all',
        ordering: 'sku_sku',
      },
    }
  )
  handler.unitPaginator = unitPaginator

  if (handler.currentPlaceStatus === 'unstarted') {
    return (
      <>
        <Typography variant="h5">Auditing</Typography>
        <GenericCard item={handler.currentPlace as Generic} />
        <Alert severity="info">{handler.currentPlaceStatus}</Alert>
        <Button
          color="primary"
          variant="contained"
          onClick={() => start()}
        >
          Start Auditing Place
        </Button>
      </>
    )
  } 

  return (
    <>
      <Typography variant="h5">Auditing</Typography>
      <GenericCard item={handler.currentPlace as Generic} />
      <Alert severity="info">{handler.currentPlaceStatus}</Alert>
      {handler.currentPlaceAccuracyScore !== undefined && <Alert severity="info">Accuracy Score: {`%${+handler.currentPlaceAccuracyScore * 100}`}</Alert>}
      {handler.currentPlaceStatus === 'started' && (
        <Grid container spacing={4}>
          <Grid item container xs={12} spacing={4}>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<ChevronLeftIcon />}
                onClick={() => exit()}
              >
                Back
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" onClick={handler.resetPlace}>
                Discard Changes
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="primary"
                variant="outlined"
                onClick={() => {
                  (document.activeElement as HTMLElement).blur()
                  save(false)
                }}
              >
                Save
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="primary"
                variant="outlined"
                endIcon={<ChevronRightIcon />}
                onClick={() => complete()}
              >
                Done
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" startIcon={<CancelIcon />} onClick={() => {
                (document.activeElement as HTMLElement).blur()
                restart()
              }}>
                Reset (Deletes all work).
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
      {handler.currentPlaceStatus === 'completed' && (
        <Grid container spacing={4}>
          <Grid item container xs={12} spacing={4}>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<ChevronLeftIcon />}
                onClick={() => exit()}
              >
                Back
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="primary"
                variant="outlined"
                startIcon={<ChevronLeftIcon />}
                onClick={() => reopen(handler.currentPlace?.object)}
              >
                Re-open
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<LaunchIcon />}
                onClick={() => window.open(`/inventory/audits/${handler.audit.object.guid}/places/${handler.currentPlace?.object.guid}` || '', "_blank")}
              >
                View Diff
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" endIcon={<ChevronRightIcon />} onClick={() => reconcileCurrent()}>
                Reconcile (Cannot be Undone)
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
      {handler.currentPlaceStatus === 'reconciled' && (
        <Grid container spacing={4}>
          <Grid item container xs={12} spacing={4}>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<ChevronLeftIcon />}
                onClick={() => exit()}
              >
                Back
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<LaunchIcon />}
                onClick={() => window.open(`/inventory/audits/${handler.audit.object.guid}/places/${handler.currentPlace?.object.guid}` || '', "_blank")}
              >
                View Diff
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" startIcon={<CancelIcon />} onClick={() => {
                (document.activeElement as HTMLElement).blur()
                restart()
              }}>
                Re-Audit
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
      {!!handler.currentContainer && (
        <>
          <GenericCard item={handler.currentContainer} />
          <IconButton onClick={handler.clearContainer}>
            <CancelIcon />
          </IconButton>
        </>
      )}
      <Grid container item justify="center" spacing={6}>
        <Grid item>
          <Button
            color="primary"
            variant={handler.mode === Mode.Scan ? 'contained' : 'outlined'}
            onClick={() => {
              handler.setMode(Mode.Scan)
            }}
          >
            Scan
          </Button>
        </Grid>
        <Grid item>
          <Button
            color="primary"
            variant={handler.mode === Mode.Unscan ? 'contained' : 'outlined'}
            onClick={() => {
              handler.setMode(Mode.Unscan)
            }}
          >
            UnScan
          </Button>
        </Grid>
      </Grid>
      <Instructions>
        <List>
          <ListItem>You are auditing {handler.currentPlace?.object.path}.</ListItem>
          <ListItem>Please scan the inventory in this place.</ListItem>
          <ListItem>
            <Typography variant="h6">Inventory not on a Container</Typography>
          </ListItem>
          <ListItem>
            1. Make sure that no container is selected. If one is selected, clear it by hitting the
            X button.
          </ListItem>
          <ListItem>2. Scan all the inventory in this place that is not on a container.</ListItem>
          <ListItem>
            <Typography variant="h6">Inventory on Containers</Typography>
          </ListItem>
          <ListItem>1. Scan a container.</ListItem>
          <ListItem>2. Scan all the inventory on that container.</ListItem>
          <ListItem>3. Scan the next container and repeat.</ListItem>
        </List>
      </Instructions>
      <Typography variant="h5">Changes</Typography>
      {isEmpty(handler.scans) ? (
        <Alert severity="warning">No changes have been made yet.</Alert>
      ) : (
        <>
          {Object.values(handler.scans)
            .filter((obj) => {
              return (
                !handler.currentContainer ||
                obj.container?.object.guid === handler.currentContainer?.object.guid
              )
            })
            .map((scan: AuditScanItem) => {
              return (
                <Grid container key={handler.key(scan)} spacing={2}>
                  {!scan.sku && scan.skus.length ? (
                    <SkuSelectionCard
                      skus={scan.skus}
                      select={(sku) => {
                        handler.setSku(scan, sku)
                      }}
                    />
                  ) : (
                    <>
                      <Grid item xs={5}>
                        {!!scan.sku && (
                          <SkuCard
                            item={scan.sku?.object}
                            selectedCase={scan.productCase}
                            selectCase={handler.caseSelector(scan)}
                            selectedBatch={scan.batch}
                            selectBatch={handler.batchSelector(scan)}
                          />
                        )}
                      </Grid>
                      <Grid item xs={5}>
                        {scan.container && <GenericCard item={scan.container} />}
                      </Grid>
                      <Grid container item xs={2}>
                        <Grid item xs={2}>
                        x
                        </Grid>
                        <Grid item xs={10}>
                          <MuiTextField value={scan.quantity >= 0 ? scan.quantity : ''} onChange={(field) => handler.updateScan(scan, field.target.value ? +field.target.value : NaN)}/>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Grid>
              )
            })}
        </>
      )}
      <Typography variant="h5">Inventory</Typography>
      {!handler.currentContainer && (
        <ContainerListTable
          paginator={containerPaginator}
          title="Containers Marked Here"
          fields={['name', 'containerType', 'path']}
          extraBulkActions={[{ label: 'Remove From Audit', action: (selected) => {
            selected.map((container) => handler.apply({scanned: makeGeneric(container), local: { quantity: 0 }}))
          }}]}
        />
      )}
      <AuditResultUnitLocListTable
        paginator={unitPaginator}
        title="Inventory Marked Here"
        fields={['sku', 'product', 'batch', 'container', 'numUnits']}
      />
    </>
  )
}

const PlaceListDisplay = (props: AuditDisplayProps) => {
  const { handler } = props
  const [tab, setTab] = useState(0)

  const placePaginator = useQueryPaginator('audit-result-places', skuit.sc.audits.resultPlaces.list, {
    params: {
      audits: handler.audit.object.guid,
      open_for_audit: handler.audit.object.guid,
      page_size: 10,
      ordering: 'place__search_path',
    },
  })

  const { mutate: reopen } = useMutation(handler.uncomplete, {
    onSuccess: () => {
      placePaginator.refresh()
      toast.success(`You have re-opened auditing the place.`)
    },
    onError: (error: AxiosError) => {
      toast.error(`Failed to re-open the place. "${error.response?.data}"`)
    },
  })

  const { mutate: reconcile } = useMutation(handler.reconcile, {
    onSuccess: () => {
      placePaginator.refresh()
      toast.success(`You have reconciled the place.`)
    },
    onError: (error: AxiosError) => {
      toast.error(`Failed to reconcile the place. "${error.response?.data}"`)
    },
  })

  handler.placePaginator = placePaginator

  const handleTabChange = (_: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue)
    if (newValue === 0) {
      placePaginator.patchFilters({
        open_for_audit: handler.audit.object.guid,
        complete_for_audit: null,
        reconciled_for_audit: null,
      })
    } else if (newValue === 1) {
      placePaginator.patchFilters({
        open_for_audit: null,
        complete_for_audit: handler.audit.object.guid,
        reconciled_for_audit: null,
      })
    } else {
      placePaginator.patchFilters({
        open_for_audit: null,
        complete_for_audit: null,
        reconciled_for_audit: handler.audit.object.guid,
      })
    }
  }

  return (
    <>
      <Alert severity="info">
        Please go to a place from the &quot;To Audit&quot; list below.
        <br />
        Scan that place to continue audit.
      </Alert>
      <Tabs value={tab} onChange={handleTabChange} aria-label="tabs">
        <Tab label="To Audit" key={0} />
        <Tab label="Complete" key={1} />
        <Tab label="Reconciled" key={1} />
      </Tabs>
      {tab === 0 && (
        <AuditResultPlaceListTable paginator={placePaginator} title="Places Left to Audit" fields={['path']} />
      )}
      {tab === 1 && (
        <AuditResultPlaceListTable
          paginator={placePaginator}
          title="Complete Places"
          fields={['path', 'accuracyScore']}
          extraRowActions={[
            {
              title: 'Audit (Re-Open)',
              onClick: (row: AuditResultPlace) => reopen(row.place),
            },
            {
              title: '--------',
              onClick: () => {},
            },
            {
              title: 'Reconcile (Cannot be undone!)',
              onClick: (row: AuditResultPlace) => reconcile(row),
            },
            {
              title: '--------',
              onClick: () => {},
            },
            {
              title: 'Open Diff >',
              onClick: (row: AuditResultPlace) => window.open(urlFor(row) || '', "_blank"),
            },
          ]}
        />
      )}
      {tab === 2 && (
        <AuditResultPlaceListTable
          paginator={placePaginator}
          title="Resolved Places"
          fields={['path', 'accuracyScore']}
          extraRowActions={[
            {
              title: 'Open Diff >',
              onClick: (row: AuditResultPlace) => window.open(urlFor(row) || '', "_blank"),
            },
          ]}
        />
      )}
    </>
  )
}
export const AuditDisplay = (props: AuditDisplayProps) => {
  const { handler } = props
  const [, refresh] = useState({})

  useEffect(() => {
    const cb: HandlerCallback = (data: any) => {
      if (data.event !== ReceivingEvent.Save) {
        refresh({})
      }
    }
    handler.registerCb(cb)
    return () => handler.removeCb(cb)
  }, [handler, refresh])

  return (
    <Grid container justify="center" spacing={4}>
      <Grid item>
        <GenericCard item={handler.audit} />
      </Grid>
      {handler.currentPlace ? (
        <Grid item xs={12}>
          <SinglePlaceDisplay handler={handler} />
        </Grid>
      ) : (
        <Grid item xs={12}>
          <PlaceListDisplay handler={handler} />
        </Grid>
      )}
    </Grid>
  )
}
